import type {
  EnrolOffer,
  EnrolOfferProduct,
  GiftRecencyFlag,
} from "@/types/mission-assign"

/**
 * OKS-1042 F3 — ONE derivation of "what is worrying about this product",
 * consumed by both the roster's control card and the confirm dialog.
 *
 * ⚠️ WHY THIS EXISTS, because the comment it replaces claimed the opposite.
 * The dialog's warning block carried the line "repeated from the control card
 * rather than recomputed, so the summary cannot disagree with the cards the
 * brand just read" — and then recomputed, from `offer.recency_by_product`
 * whole: never filtered to the chosen product, with stock state absent from
 * the source set entirely, rendering a bare `product_name` instead of a
 * sentence. Enrolling a creator on an exhausted « Trousse » therefore listed a
 * DIFFERENT product's name and omitted the exhausted warning, on the last
 * screen before an irreversible write.
 *
 * A comment asserting an invariant is not the invariant. Two call sites
 * deriving the same fact will drift; the only structural fix is that there is
 * one derivation and neither site can express a second one.
 *
 * ⚠️ `answerable` and `flag` are two separate facts (OKS-1042 T-9):
 * `answerable: false` means the guard could not run, a null flag means it ran
 * and flagged nobody. Both render as silence, and only the conjunction is a
 * warning.
 */
export interface ProductWarnings {
  /** Stock is out. `remaining` is clamped at 0, so this alone under-reports. */
  exhausted: boolean
  /** How far past stock the consuming set goes. A DIFFERENT fact from
   *  `exhausted`, not a louder one (OKS-990 R14b). */
  overBy: number
  /** Non-null only when the guard ran AND flagged this creator. */
  recencyFlag: GiftRecencyFlag | null
}

export function productWarnings(
  product: EnrolOfferProduct,
  offer: EnrolOffer | null | undefined,
): ProductWarnings {
  const slot = offer?.gift_slots?.by_product?.find(
    (row) => row.mission_product_id === product.mission_product_id,
  )
  const recency = offer?.recency_by_product?.find(
    (row) => row.mission_product_id === product.mission_product_id,
  )

  return {
    exhausted: slot ? slot.remaining === 0 : false,
    overBy: slot ? Math.max(slot.consuming_count - slot.stock, 0) : 0,
    recencyFlag:
      recency?.answerable === true && recency.flag !== null
        ? recency.flag
        : null,
  }
}

export function hasWarnings(w: ProductWarnings): boolean {
  return w.overBy > 0 || w.exhausted || w.recencyFlag !== null
}

/**
 * The assign preview's own branching, not a second expression of it: in FR
 * `days_ago === 0` is its own sentence, and a `count: 0` would render "il y a
 * 0 j".
 */
export function recencySentence(
  t: (k: string, o?: Record<string, unknown>) => string,
  flag: GiftRecencyFlag,
): string {
  const label = flag.variant_label
  if (flag.days_ago === 0) {
    return label !== null
      ? t("missions.assign.preview.recency.today_with_variant", { label })
      : t("missions.assign.preview.recency.today_product_only", {
          product: flag.product_name,
        })
  }
  return label !== null
    ? t("missions.assign.preview.recency.with_variant", {
        label,
        count: flag.days_ago,
      })
    : t("missions.assign.preview.recency.product_only", {
        product: flag.product_name,
        count: flag.days_ago,
      })
}

/**
 * The stock sentence, or null when there is none.
 *
 * ⚠️ Key names were MEASURED, not guessed: the pair is
 * `gift_slots_product_exhausted` / `_product_over`, both interpolating
 * `%{name}`. `gift_slots_exhausted` does not exist.
 */
export function stockSentence(
  t: (k: string, o?: Record<string, unknown>) => string,
  product: EnrolOfferProduct,
  w: ProductWarnings,
): string | null {
  if (w.overBy > 0) {
    return t("missions.assign.preview.gift_slots_product_over", {
      name: product.name,
      count: w.overBy,
    })
  }
  if (w.exhausted) {
    return t("missions.assign.preview.gift_slots_product_exhausted", {
      name: product.name,
    })
  }
  return null
}

/**
 * Every warning sentence for ONE product, in the card's own order (stock, then
 * recency). The dialog renders exactly these strings, so "the dialog repeats
 * the warnings" is true by construction rather than by comment.
 */
export function warningSentences(
  t: (k: string, o?: Record<string, unknown>) => string,
  product: EnrolOfferProduct,
  offer: EnrolOffer | null | undefined,
): string[] {
  const w = productWarnings(product, offer)
  return [
    stockSentence(t, product, w),
    w.recencyFlag ? recencySentence(t, w.recencyFlag) : null,
  ].filter((s): s is string => s !== null)
}
